// DJ Self-Service Events API client (Phase 1, P6).
// Wraps the approved-DJ event / poster / series endpoints from Gates C–E and the
// minor P6 performer extension. All calls go through the Bearer-authenticated
// `api` service. Read the design spec: docs/DJ_EVENT_CREATION_PHASE1_PLAN.md.
import api from '@/services/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '/api';

// ---- DJ approval status -----------------------------------------------------
// GET /api/users/me/dj-application -> { status, public_profile_url, ... }.
// Resolves to { status:null } when there is no application (404).
export async function fetchMyDjApplication() {
  try {
    return await api.get('/users/me/dj-application');
  } catch (_) {
    return { status: null };
  }
}

export async function isApprovedDj() {
  const app = await fetchMyDjApplication();
  return app && app.status === 'approved';
}

// ---- DJ events (one-time + series mixed) ------------------------------------
// GET /api/dj/events?tab=&status= -> owner's own events AND series (series carry
// recurring:true). One-time events use the event view; series the series summary.
export function listMyDjEvents({ tab, status } = {}) {
  const qs = new URLSearchParams();
  if (tab) qs.set('tab', tab);
  if (status) qs.set('status', status);
  const q = qs.toString();
  return api.get(`/dj/events${q ? `?${q}` : ''}`);
}

export function getDjEvent(eventId) {
  return api.get(`/dj/events/${encodeURIComponent(eventId)}`);
}

// Create a one-time event OR a recurring series (when payload.recurrence is set).
export function createDjEvent(payload) {
  return api.post('/dj/events', payload);
}

export function updateDjEvent(eventId, payload) {
  return api.put(`/dj/events/${encodeURIComponent(eventId)}`, payload);
}

export function publishDjEvent(eventId) {
  return api.post(`/dj/events/${encodeURIComponent(eventId)}/publish`, {});
}

export function deleteDjEvent(eventId) {
  return api.del(`/dj/events/${encodeURIComponent(eventId)}`);
}

// ---- Recurring series -------------------------------------------------------
export function getDjSeries(seriesId) {
  return api.get(`/dj/series/${encodeURIComponent(seriesId)}`);
}

// scope: 'series' | 'occurrence' (occurrence requires occurrenceDate).
export function updateDjSeries(seriesId, payload, { scope = 'series', occurrenceDate } = {}) {
  const qs = new URLSearchParams({ scope });
  if (occurrenceDate) qs.set('occurrence_date', occurrenceDate);
  return api.put(`/dj/series/${encodeURIComponent(seriesId)}?${qs.toString()}`, payload);
}

export function deleteDjSeries(seriesId, { scope = 'series', occurrenceDate } = {}) {
  const qs = new URLSearchParams({ scope });
  if (occurrenceDate) qs.set('occurrence_date', occurrenceDate);
  return api.del(`/dj/series/${encodeURIComponent(seriesId)}?${qs.toString()}`);
}

// ---- Posters ----------------------------------------------------------------
// Staged upload before an event exists -> { poster_key, url }. Multipart; the
// browser sets the boundary. Bearer auth via the api service's token.
export async function uploadEventPosterStaged(file) {
  return uploadMultipart('/dj/events/posters/upload', file);
}

// Upload + attach a poster directly to an owned event -> updated owner view.
export async function addEventPoster(eventId, file) {
  return uploadMultipart(`/dj/events/${encodeURIComponent(eventId)}/posters`, file);
}

export function deleteEventPoster(eventId, key) {
  return api.del(`/dj/events/${encodeURIComponent(eventId)}/posters/${encodeURIComponent(key)}`);
}

export function reorderEventPosters(eventId, posters) {
  return api.put(`/dj/events/${encodeURIComponent(eventId)}/posters`, { posters });
}

// ---- Performers (lineup) ----------------------------------------------------
// Public lineup for an event id: { approved_djs:[{id,slug,dj_name,...}], guest_djs:[name] }.
export function fetchEventLineup(eventId) {
  return api.get(`/events/${encodeURIComponent(eventId)}/djs`);
}

// Approved DJ directory (public) for the performer picker.
export function fetchApprovedDjs() {
  return api.get('/djs');
}

// ---- internal: multipart helper (mirrors api.uploadDJImage) ----------------
async function uploadMultipart(endpoint, file) {
  const token = api.getAccessToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BACKEND}${endpoint}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
    credentials: 'include',
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Upload failed (${res.status})`);
  }
  return res.json();
}

// ---- client-side poster validation (before upload) --------------------------
export const POSTER_MAX_BYTES = 5 * 1024 * 1024;
export const POSTER_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const POSTER_ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp'];

export function validatePosterFile(file) {
  if (!file) return 'No file selected.';
  const ext = (file.name || '').toLowerCase().split('.').pop();
  if (!POSTER_ALLOWED_TYPES.includes(file.type) && !POSTER_ALLOWED_EXT.includes(ext)) {
    return 'Only JPG, PNG, or WebP images are allowed.';
  }
  if (file.size > POSTER_MAX_BYTES) {
    return 'Image is too large (max 5 MB).';
  }
  return null;
}

// Resolve a poster key/path to a displayable URL (same-origin /api by default).
export function posterUrl(keyOrPath) {
  if (!keyOrPath) return null;
  if (/^https?:\/\//i.test(keyOrPath)) return keyOrPath;
  if (keyOrPath.startsWith('/api/')) return keyOrPath;
  if (/^[a-f0-9]{32}\.(jpg|jpeg|png|webp)$/i.test(keyOrPath)) {
    return `${BACKEND}/events/poster/${keyOrPath}`;
  }
  return keyOrPath;
}

// US states for the location dropdown (50 + DC), matching the backend allowlist.
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL',
  'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];
